import React, { useEffect, useState } from "react"; // Import useEffect
import { SiteData2 } from "types/website-tester";
import { CheckCircle, XCircle, AlertTriangle } from "lucide-react";
import { CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import KeywordList from "./KeywordList";
import ContactFormOnlyChaka from "../common/ContactFormOnlyChaka"

//check if a string contains any of the keywords 
const containsKeywords = (text: string | undefined, keywords: string[]): boolean => {
  if (!text) return false;
  return keywords.some(keyword => text.toLowerCase().includes(keyword.toLowerCase()));
};

// parse speed values like "2.4 s" into numbers
const parseSpeedValue = (value: string | undefined): number => {
  if (!value) return 0;
  return parseFloat(value.replace(/[^0-9.]/g, '')); // Remove non-numeric characters and parse
};

//status icons 
const statusIcon = (status: boolean | undefined) => {
  if (status === undefined) return <AlertTriangle color="orange" />; // Show warning for undefined
  return status ? <CheckCircle color="green" /> : <span className="pe-1"><XCircle color="red" size="20" style={{ marginTop: -5 }} /></span>;
};

//@ts-ignore
const BasicSeoContent = ({ siteData, hasBasicSeoFailures, score }) => {
  const titlePass = containsKeywords(siteData?.title, siteData?.keywords || []);
  const metaPass = containsKeywords(siteData?.metaDescription, siteData?.keywords || []);
  const ogTitlePass = containsKeywords(siteData?.ogTitle, siteData?.keywords || []);
  const ogDescPass = containsKeywords(siteData?.ogDescription, siteData?.keywords || []);
  const ogPass = ogTitlePass && ogDescPass;
  const canonicalPass = !!siteData?.canonical;
  const sitemapPass = !!siteData?.sitemap;
  const robotsPass = !!siteData?.robotsTxt?.exists;
  const llmsPass = !!siteData?.llmsTxt?.exists;
  const schemaMarkupPresent = !!siteData?.schemaMarkup?.found;

  //for images, combine desktop and mobile elements
  const allImages = [
    ...(siteData?.desktop?.elements || []),
    ...(siteData?.mobile?.elements || []),
  ].filter((element) => element.type === "img");

  const imagesAltPass = allImages.every(
    (element) => element.alt && containsKeywords(element.alt, siteData?.keywords || [])
  );
  const filenamesPass = allImages.every((element) =>
    containsKeywords(element.filename, siteData?.keywords || [])
  );

  // Determine color based on score
  const getProgressColor = (score: number) => {
    if (score >= 0 && score <= 49) return '#dc3545'; // Red
    if (score >= 50 && score <= 69) return '#fd7e14'; // Orange
    if (score >= 70 && score <= 100) return '#198754'; // Green (Bootstrap 5 green)
    return '#0d6efd'; // Default blue (Bootstrap 5 blue)
  };

  return (
    <section className="mt-6">
      <div style={{ width: 100, height: 100 }}>
        <CircularProgressbar
          value={score}
          text={`${score}%`}
          styles={{
            path: { stroke: getProgressColor(score) },
            text: { fill: getProgressColor(score) }
          }}
        />
      </div>
      {hasBasicSeoFailures && (
        <h2 className="text-xl font-semibold border-b pb-2 mt-4">Basic SEO</h2>
      )}

      {/* Always show Keyword List */}
      <KeywordList keywords={siteData?.keywords || []} />
      {siteData?.keywordPositions &&
        Object.entries(siteData.keywordPositions)
          .filter(([_, position]) => (String(position).toLowerCase() !== 'error'))
          .map(([keyword, position]) => {
            const isNotFound = position === -1 || position === "Not in top 100";
            return (
              <p className="mb-3" key={keyword}>
                Your main industry keyword, "{keyword}"{" "}
                {isNotFound
                  ? "is not in top 100 in Google results."
                  : "is number " + position + " in Google results."}
              </p>
            );
          })}

      <sup className="supIt" style={{ lineHeight: "1.25", display: "block" }}>
        Find out more keyword rankings and competitor data with our full <a href="#full_report">SEO Audit</a> or ask for a free consultation about how we can help you drive more sales.
      </sup>
      {(() => {
        const failingSections = [];

        if (!llmsPass) failingSections.push(
          <div key="llms" className="mt-3">
            <h3 className="text-lg font-medium">ChatGPT/AI</h3>
            <p>{statusIcon(llmsPass)} Missing llms.txt<br />
              llms.txt is a text file that tells ChatGPT and other AI/LLM systems how to crawl, index, and interact with your website.</p>
            <p>Add one to enhance your appearance in ChatGPT and other AI chatbots.</p>
          </div>
        );
        if (!titlePass) failingSections.push(
          <div key="title" className="mt-3">
            <h3 className="text-lg font-medium">Title</h3>
            <p>The title appears in browser tabs and search results.</p>
            <p>{statusIcon(titlePass)}
              <strong>Keywords are not in the title</strong>
              <br />If you don't include keywords in the title, it makes it harder for people to find your page.
            </p>
          </div>
        );

        if (!metaPass) failingSections.push(
          <div key="meta" className="mt-3">
            <h3 className="text-lg font-medium">Meta Description</h3>
            <p>This is a short summary shown in search results.</p>
            <p>{statusIcon(metaPass)}
              <strong>Keywords are not in Meta Description</strong>
              <br />It does not contain keywords, so people searching will probably not click on your link in Google results.
            </p>
          </div>
        );

        if (!ogTitlePass || !ogDescPass) failingSections.push(
          <div key="og">
            <h3 className="mt-3 text-lg font-medium">OG Information</h3>
            <p>This is the information used when people share your page on social media.</p>
            {!ogTitlePass && (
              <p>{statusIcon(ogTitlePass)}<strong>Keywords are not in OG Title</strong> <br />Doesn't use search keywords; this reduces visibility in social media.</p>
            )}
            {!ogDescPass && (
              <p>{statusIcon(ogDescPass)}<strong>Keywords are not in OG Description</strong> <br />Missing keywords in the description, so you won't have much engagement on shares on social media.</p>
            )}
          </div>
        );

        if (!canonicalPass) failingSections.push(
          <div key="canonical" className="mt-3">
            <h3 className="text-lg font-medium">{statusIcon(canonicalPass)} Canonical URL</h3>
            <p>Tells search engines which version of a page is the "main" one.</p>
            <p>
              <strong>There is no canonical URL specified.</strong>
              <br />Without it, duplicate content can confuse Google and affect rankings.
            </p>
          </div>
        );

        if (!sitemapPass) failingSections.push(
          <div key="sitemap" className="mt-3">
            <h3 className="text-lg font-medium">{statusIcon(sitemapPass)} Sitemap</h3>
            <p>Help search engines navigate your site by including an up-to-date sitemap. Missing sitemap may lead to very poor Google results.</p>
          </div>
        );

        if (!robotsPass) failingSections.push(
          <div key="robots" className="mt-3">
            <h3 className="text-lg font-medium">{statusIcon(robotsPass)} Robots.txt</h3>
            <p>Missing robots.txt may lead to very poor Google results.</p>
          </div>
        );

        if (!imagesAltPass || !filenamesPass) failingSections.push(
          <div key="images" className="mt-3">
            <h3 className="text-lg font-medium">Image Alt Tags and Filenames</h3>
            <p>
              {!imagesAltPass && (
                <>{statusIcon(imagesAltPass)}
                  <strong>All Images Do Not Have Alt Tags That Contain Keywords</strong>
                  <p>Images need explanatory text for people with disabilities. If you don't include them, Google marks your content down.</p>
                </>
              )}
              {!filenamesPass && (
                <>{statusIcon(filenamesPass)}
                  <strong>All Filenames Do Not Contain Keywords</strong>
                  <p>The actual filename of your images can contain keywords and this give you a little SEO boost.</p>
                </>
              )}
            </p>
          </div>
        );

        if (!schemaMarkupPresent) failingSections.push(
          <div key="schema" className="mt-3">
            <h3 className="text-lg mb-0 font-medium">{statusIcon(schemaMarkupPresent)} Schema Markup</h3>
            <p>Special tags that help search engines understand your content better.</p>
            <p><strong>Schema markup is missing or invalid.</strong></p>
            <p>Missing or broken markup – rich results (like review stars or product info) won't show.</p>
          </div>
        );

        // Show only the first 3 failing sections
        const displayedSections = failingSections
        // const displayedSections = failingSections.slice(0, 3);

        return (
          <>
            {displayedSections}

            {/* Always show Keyword Density */}
            {(() => {
              const kdObj = siteData?.keywordDensity?.keyword_density || {};
              const kdValues = Object.values(kdObj);
              //@ts-expect-error - gen
              const sumDensity = kdValues.reduce((acc, val) => acc + (val || 0), 0);
              //@ts-expect-error - gen
              const averageDensity = kdValues.length ? sumDensity / kdValues.length : 0;
              const keywordDensityFail = (averageDensity < 3) || (averageDensity >= 4);
              let keywordDensityMessage = "";
              if (averageDensity < 3) {
                keywordDensityMessage = "There are not enough mentions of your keywords on your homepage.";
              } else if (averageDensity >= 4) {
                keywordDensityMessage = "There is keyword stuffing in your website.";
              }
              if (!keywordDensityFail) return null;
              return (
                <div className="mt-3">
                  <h3 className="text-lg font-medium">{statusIcon(false)}Keyword Density</h3>
                  <p>{keywordDensityMessage}</p>
                </div>
              );
            })()}
          </>
        );
      })()}
    </section>
  );
};

const BASIC_SEO_WEIGHTS = {
  title: 9,
  metaDescription: 9,
  ogTitle: 3,
  ogDescription: 3,
  canonical: 3,
  sitemap: 9,
  robotsTxt: 3,
  llmsTxt: 5,
  imagesAlt: 10,
  filenames: 9,
  keywordDensity: 9,
  schemaMarkup: 3,
};

//@ts-ignore
const calculateBasicSeoScore = (siteData) => {
  let score = 0;
  if (containsKeywords(siteData?.title, siteData?.keywords || [])) score += BASIC_SEO_WEIGHTS.title;
  if (containsKeywords(siteData?.metaDescription, siteData?.keywords || [])) score += BASIC_SEO_WEIGHTS.metaDescription;
  if (containsKeywords(siteData?.ogTitle, siteData?.keywords || []) && containsKeywords(siteData?.ogDescription, siteData?.keywords || [])) score += BASIC_SEO_WEIGHTS.ogTitle + BASIC_SEO_WEIGHTS.ogDescription;
  if (siteData?.canonical) score += BASIC_SEO_WEIGHTS.canonical;
  if (siteData?.sitemap) score += BASIC_SEO_WEIGHTS.sitemap;
  if (siteData?.robotsTxt?.exists) score += BASIC_SEO_WEIGHTS.robotsTxt;
  if (siteData?.llmsTxt?.exists) score += BASIC_SEO_WEIGHTS.llmsTxt;

  //scoring for images and keyword density
  const allImages = [
    ...(siteData?.desktop?.elements || []),
    ...(siteData?.mobile?.elements || []),
  ].filter((element) => element.type === "img");

  const imagesAltPass = allImages.every(
    (element) => element.alt && containsKeywords(element.alt, siteData?.keywords || [])
  );
  const filenamesPass = allImages.every((element) =>
    containsKeywords(element.filename, siteData?.keywords || [])
  );

  if (imagesAltPass) score += BASIC_SEO_WEIGHTS.imagesAlt;
  if (filenamesPass) score += BASIC_SEO_WEIGHTS.filenames;

  const kdObj = siteData?.keywordDensity?.keyword_density || {};
  const kdValues = Object.values(kdObj);
  //@ts-ignore
  const sumDensity = kdValues.reduce((acc, val) => acc + val, 0);
  //@ts-ignore
  const averageDensity = kdValues.length ? sumDensity / kdValues.length : 0;
  if (averageDensity >= 3 && averageDensity < 4) score += BASIC_SEO_WEIGHTS.keywordDensity;

  // SCHEMA MARKUP: use .found not .status
  if (siteData?.schemaMarkup?.found) score += BASIC_SEO_WEIGHTS.schemaMarkup;

  //the total possible score
  const totalWeight = Object.values(BASIC_SEO_WEIGHTS).reduce((acc, weight) => acc + weight, 0);
  return Math.round((score / totalWeight) * 100);
};

const PerformanceContent = ({
  //@ts-expect-error - gen
  siteData,
  //@ts-expect-error - gen
  pageSpeedData,
  //@ts-expect-error - gen
  hasPerformanceFailure,
  //@ts-expect-error - gen
  score, // This is Google's score - keep it!
  //@ts-expect-error - gen
  performValues,
  //@ts-expect-error - gen
  performanceView,
  //@ts-expect-error - gen
  setPerformanceView
}) => {
  const parseSizeValue = (
    //@ts-expect-error - gen
    // 
    str) => {
    if (!str) return 0;
    const match = str.match(/\d+/);
    return match ? parseInt(match[0]) : 0;
  };

  // Check individual metrics - these are just INDICATORS, not for scoring
  const speedIndexSlow = parseSpeedValue(pageSpeedData.speedIndex) > performValues.speed;
  const fcpSlow = parseSpeedValue(pageSpeedData.firstContentfulPaint) > performValues.fcp;
  const interactiveSlow = parseSpeedValue(pageSpeedData.interactive) > performValues.interact;

  const hasResponsiveImageIssues = pageSpeedData.usesResponsiveImages !== "N/A" &&
    pageSpeedData.usesResponsiveImages.includes("savings");
  const hasUnminifiedJS = pageSpeedData.unminifiedJavaScript !== "N/A" &&
    pageSpeedData.unminifiedJavaScript.includes("savings");
  const hasUnminifiedCSS = pageSpeedData.unminifiedCSS !== "N/A" &&
    pageSpeedData.unminifiedCSS.includes("savings");
  const hasRenderBlocking = pageSpeedData.renderBlockingResources !== "N/A" &&
    pageSpeedData.renderBlockingResources.includes("savings");

  const byteWeightHigh = parseSizeValue(pageSpeedData.totalByteWeight) > 2000;
  const tooManyRequests = pageSpeedData.networkRequests !== "N/A" &&
    parseInt(pageSpeedData.networkRequests) > 100;
  const hasRedirects = pageSpeedData.redirects !== "N/A" &&
    !pageSpeedData.redirects.includes("0 ms");

  const contentLengthIssue = siteData?.contentLength?.status === false;
  const headingHierarchyIssue = !siteData?.headingHierarchy?.status;

  const failingPerformanceSections = [];

  // Only show sections that actually have issues
  if (speedIndexSlow || fcpSlow || interactiveSlow) {
    failingPerformanceSections.push(
      <div key="pageSpeed" className="mt-3">
        <h3 className="text-lg font-medium">Page Speed Issues</h3>
        <div>
          {speedIndexSlow && (
            <p>{statusIcon(false)}
              <strong>Speed Index:</strong> {pageSpeedData.speedIndex}
              <br />Your page loads slowly for visitors.
            </p>
          )}
          {fcpSlow && (
            <p>{statusIcon(false)}
              <strong>First Content Paint:</strong> {pageSpeedData.firstContentfulPaint}
              <br />First content takes too long to appear.
            </p>
          )}
          {interactiveSlow && (
            <p>{statusIcon(false)}
              <strong>Time to Interactive:</strong> {pageSpeedData.interactive}
              <br />Visitors have to wait before they can interact with your site.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (hasResponsiveImageIssues || hasUnminifiedJS || hasUnminifiedCSS || hasRenderBlocking) {
    failingPerformanceSections.push(
      <div key="resourceOpt" className="mt-3">
        <h3 className="text-lg font-medium">Resource Optimization</h3>
        <div>
          {hasResponsiveImageIssues && (
            <p>{statusIcon(false)}
              <strong>Responsive Images:</strong> {pageSpeedData.usesResponsiveImages}
              <br />Images aren't optimized for different screen sizes.
            </p>
          )}
          {hasUnminifiedJS && (
            <p>{statusIcon(false)}
              <strong>Unminified JavaScript:</strong> {pageSpeedData.unminifiedJavaScript}
              <br />JavaScript files should be compressed.
            </p>
          )}
          {hasUnminifiedCSS && (
            <p>{statusIcon(false)}
              <strong>Unminified CSS:</strong> {pageSpeedData.unminifiedCSS}
              <br />CSS files should be compressed.
            </p>
          )}
          {hasRenderBlocking && (
            <p>{statusIcon(false)}
              <strong>Render Blocking Resources:</strong> {pageSpeedData.renderBlockingResources}
              <br />These delay your homepage from showing.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (byteWeightHigh || tooManyRequests || hasRedirects) {
    failingPerformanceSections.push(
      <div key="contentDelivery" className="mt-3">
        <h3 className="text-lg font-medium">Content Delivery</h3>
        <div>
          {byteWeightHigh && (
            <p>{statusIcon(false)}
              <strong>Total Page Size:</strong> {pageSpeedData.totalByteWeight}
              <br />Page is too heavy - should be under 2MB.
            </p>
          )}
          {tooManyRequests && (
            <p>{statusIcon(false)}
              <strong>Network Requests:</strong> {pageSpeedData.networkRequests}
              <br />Too many files being loaded - aim for under 100.
            </p>
          )}
          {hasRedirects && (
            <p>{statusIcon(false)}
              <strong>Redirects:</strong> {pageSpeedData.redirects}
              <br />Redirects slow down your site.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (contentLengthIssue || headingHierarchyIssue) {
    failingPerformanceSections.push(
      <div key="other" className="mt-3">
        <h3 className="text-lg font-medium">Other Issues</h3>
        <div>
          {contentLengthIssue && (
            <p>{statusIcon(false)}
              <strong>Content Length:</strong> {siteData.contentLength.message}
              <br />Too little content may hurt SEO.
            </p>
          )}
          {headingHierarchyIssue && (
            <p>{statusIcon(false)}
              <strong>Heading Hierarchy:</strong> Improper structure
              <br />Headings should follow logical order (H1, H2, H3).
            </p>
          )}
        </div>
      </div>
    );
  }

  const getProgressColor = (score: number) => {
    if (score >= 0 && score <= 49) return '#dc3545';
    if (score >= 50 && score <= 69) return '#fd7e14';
    if (score >= 70 && score <= 100) return '#198754';
    return '#0d6efd';
  };

  return (
    <section className="mt-6">
      <div style={{ width: 100, height: 100 }}>
        <CircularProgressbar
          value={score}
          text={`${score}%`}
          styles={{
            path: { stroke: getProgressColor(score) },
            text: { fill: getProgressColor(score) }
          }}
        />
      </div>

      <h2 className="text-xl font-semibold border-b pb-2 mt-4">Performance</h2>
      <p className="text-sm mb-3">Score from Google PageSpeed Insights</p>

      <div className="d-flex justify-content-center mb-3">
        <div className="btn-group">
          <button
            className={`btn ${performanceView === 'desktop' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setPerformanceView('desktop')}
          >
            Desktop
          </button>
          <button
            className={`btn ${performanceView === 'mobile' ? 'btn-primary' : 'btn-outline-primary'}`}
            onClick={() => setPerformanceView('mobile')}
          >
            Mobile
          </button>
        </div>
      </div>

      {failingPerformanceSections.length > 0 ? (
        <>
          <p className="mb-3"><strong>Issues detected:</strong></p>
          {failingPerformanceSections}
        </>
      ) : (
        <p className="text-success">✓ No major performance issues detected!</p>
      )}
      {/* CONDITIONAL ADDITIONAL FACTORS */}
      <div className="mt-4 p-3" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #0d6efd' }}>
        <p className="mb-2"><strong>Google PageSpeed also tests:</strong></p>
        <ul className="mb-2" style={{ fontSize: '0.9em', lineHeight: '1.6' }}>
          {/* Only show if we don't detect responsive image issues */}
          {!hasResponsiveImageIssues && (
            <li><strong>Image optimization:</strong> Modern formats (WebP), proper sizing, lazy loading</li>
          )}

          {/* Only show if we don't detect JS/CSS/render-blocking issues */}
          {!hasUnminifiedJS && !hasUnminifiedCSS && !hasRenderBlocking && (
            <li><strong>Code optimization:</strong> Minified JavaScript/CSS, eliminating render-blocking</li>
          )}

          {/* Only show if we don't detect network request issues */}
          {!tooManyRequests && (
            <li><strong>Request efficiency:</strong> Reducing HTTP requests, combining files</li>
          )}

          {/* Always show - we never detect these */}
          <li><strong>Server performance:</strong> Hosting speed, response times, CDN usage</li>
          <li><strong>Browser caching:</strong> Storing assets to reduce repeat downloads</li>
          <li><strong>Third-party scripts:</strong> Impact from tracking, ads, social plugins</li>
        </ul>
        <p className="mb-0" style={{ fontSize: '0.9em' }}>
          Our <a href="#full_report"><strong>full SEO audit</strong></a> analyzes all {failingPerformanceSections.length > 0 ? 'these factors' : 'performance factors'} in detail
          with specific fixes to improve your score.
        </p>
      </div>
    </section>
  );
};

//Report component
//@ts-expect-error gen
const Report: React.FC<SiteData2> = ({ siteData, domain, pageSpeed, pageSpeedMobile }) => {
  const [performanceDataLoaded, setPerformanceDataLoaded] = useState(false);
  const [performanceView, setPerformanceView] = useState<'mobile' | 'desktop'>('mobile');

  useEffect(() => {
    console.log(domain)
  }, [siteData, domain]);

  useEffect(() => {
    if (pageSpeed || pageSpeedMobile) {
      if (pageSpeed) pageSpeed.device = 'desktop';
      if (pageSpeedMobile) pageSpeedMobile.device = 'mobile';
      setPerformanceDataLoaded(true);
    } else {
      setPerformanceDataLoaded(false);
    }
  }, [pageSpeed, pageSpeedMobile]);

  const basicSeoScore = calculateBasicSeoScore(siteData);
  const performanceScore = (performanceDataLoaded && pageSpeed) ? pageSpeed.performanceScore : 0;
  const performanceScoreMobile = (performanceDataLoaded && pageSpeedMobile) ? pageSpeedMobile.performanceScore : 0;

  const hasBasicSeoFailures = basicSeoScore < 100;
  const hasPerformanceFailureDesktop = performanceDataLoaded && pageSpeed && performanceScore < 100;
  const hasPerformanceFailureMobile = performanceDataLoaded && pageSpeedMobile && performanceScoreMobile < 100;

  return (
    <div className="container" style={{ maxWidth: 1000 }}>
      <h2 className="display-4 mt-10 pb-3" style={{ textAlign: "center" }}>Report</h2>
      <p className="pb-3" style={{ textAlign: "center", display: "block", margin: "0 auto" }}>
        Here is an initial analysis of your homepage landing screen.
        This shows issues with your Basic SEO and Google's measurement of the site's performance.  </p>
      <p className="pb-3" style={{ textAlign: "center", display: "block", margin: "0 auto" }}>Bear in mind that there are <em>lots of other factors that affect ranking</em>, like getting <strong>Good Quality Backlinks</strong> and having <strong>Rich Content</strong> throughout your site.
        We prioritise only tasks that will make your site succeed and convert more customers.
        So <a href="#full_report">get in touch</a> if you'd like to discuss your SEO and start improving your exposure today.
      </p>
      <div className="row g-10 pb-10">
        {hasBasicSeoFailures && (
          <div className="col-md-6" >
            <BasicSeoContent siteData={siteData} hasBasicSeoFailures={hasBasicSeoFailures} score={basicSeoScore} />
          </div>
        )}
        <div className="col-md-6">
          {!performanceDataLoaded ? (
            <div className="loading-placeholder">
              <section className="mt-6">
                <div style={{ width: 100, height: 100 }}>
                  <CircularProgressbar value={0} text={`0%`} />
                </div>
                <h2 className="text-xl font-semibold border-b pb-2 mt-4">Performance Loading..</h2>
              </section>
            </div>
          ) : (hasPerformanceFailureDesktop || hasPerformanceFailureMobile) && (
            <PerformanceContent
              siteData={siteData}
              pageSpeedData={performanceView === 'desktop' ? pageSpeed : pageSpeedMobile}
              hasPerformanceFailure={performanceView === 'desktop' ? hasPerformanceFailureDesktop : hasPerformanceFailureMobile}
              score={performanceView === 'desktop' ? performanceScore : performanceScoreMobile}
              performValues={performanceView === 'desktop' ? { speed: 2, fcp: 2, interact: 3 } : { speed: 3, fcp: 3, interact: 4 }}
              performanceView={performanceView}
              setPerformanceView={setPerformanceView}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default Report;
